import { ArrowRight, Bot, Database, Layers } from 'lucide-react'
import { useOnboardingSheet } from '@/components/onboarding-sheet/context'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import { useMixpanel, useRBAC } from '@/lib/hooks'
import { RoleID } from '@/lib/rbac'
import { cn } from '@/lib/utils'
import { SevallaLogo } from '@/logos/sevalla'
import type { OnboardingPath } from '@/components/onboarding-sheet/context'

const AGENT_TILE_ROLE_IDS: string[] = [
  RoleID.COMPANY_OWNER,
  RoleID.COMPANY_ADMIN,
]

type Props = {
  onSelectPath: (path: OnboardingPath) => void
}

export const CtaView = ({ onSelectPath }: Props) => {
  const { dismiss, setSelectedPath, setView, entrypoint } = useOnboardingSheet()
  const { rbac, rolesInCompany } = useRBAC()
  const { track } = useMixpanel()
  const canConnectAgent =
    rbac.kinsta().superadmin.can() ||
    rolesInCompany.some((role) => AGENT_TILE_ROLE_IDS.includes(role))

  const trackIfSignup = () => {
    if (entrypoint === 'signup') {
      track('Onboarding guides visited', { entrypoint: 'signup' })
    }
  }

  const handleAgent = () => {
    trackIfSignup()
    setSelectedPath('agent')
    setView('agent-select')
  }

  const handleDeploy = () => {
    trackIfSignup()
    onSelectPath('deploy')
  }

  const handleDatabase = () => {
    trackIfSignup()
    onSelectPath('database')
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto p-6 space-y-6">
      <div className="flex flex-col items-center gap-2 pt-2 pb-4">
        <SevallaLogo className="h-6 w-auto" />
        <h2 className="text-xl font-semibold mt-3">Welcome to Sevalla</h2>
        <p className="text-sm text-muted-foreground text-center">
          How would you like to get started?
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {canConnectAgent && (
          <Item
            asChild
            className={cn(
              'cursor-pointer overflow-hidden rounded-xl p-5 text-left transition-all duration-200',
              'bg-linear-115 from-[#FF7600] via-[#FF9146] to-[#FFBB8D]',
              'dark:from-orange-700 dark:via-orange-600 dark:to-orange-500',
              'border-white/20 hover:shadow-xl hover:scale-[1.01] active:scale-[0.99]'
            )}
          >
            <button onClick={handleAgent}>
              <ItemMedia className="size-11 rounded-xl border border-white/30 bg-white/20 backdrop-blur-sm">
                <Bot className="size-6 text-white" />
              </ItemMedia>
              <ItemContent>
                <ItemTitle className="text-white">
                  Connect an AI agent
                  <Badge
                    variant="secondary"
                    className="bg-white/20 border border-white/30 text-white"
                  >
                    Recommended
                  </Badge>
                </ItemTitle>
                <ItemDescription className="text-white/80">
                  Connect Claude Code, Cursor, Windsurf, or others and ship
                  without leaving your coding environment.
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <ArrowRight className="size-4 text-white/70 transition-transform group-hover/item:translate-x-1" />
              </ItemActions>
            </button>
          </Item>
        )}

        <Item
          asChild
          variant="outline"
          className="cursor-pointer rounded-xl text-left hover:bg-accent/50 hover:shadow-sm"
        >
          <button onClick={handleDeploy}>
            <ItemMedia variant="icon">
              <Layers className="text-muted-foreground" />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>Deploy your own service</ItemTitle>
              <ItemDescription>
                Deploy an application, database, static site, or object storage.
              </ItemDescription>
            </ItemContent>
            <ItemActions>
              <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover/item:translate-x-1" />
            </ItemActions>
          </button>
        </Item>

        <Item
          asChild
          variant="outline"
          className="cursor-pointer rounded-xl text-left hover:bg-accent/50 hover:shadow-sm"
        >
          <button onClick={handleDatabase}>
            <ItemMedia variant="icon">
              <Database className="text-muted-foreground" />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>Deploy a database</ItemTitle>
              <ItemDescription>
                Spin up a managed database in seconds.
              </ItemDescription>
            </ItemContent>
            <ItemActions>
              <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover/item:translate-x-1" />
            </ItemActions>
          </button>
        </Item>

      </div>

      <div className="mt-auto pt-2 flex justify-center">
        <Button variant="ghost" size="sm" onClick={dismiss}>
          Skip for now
        </Button>
      </div>
    </div>
  )
}
